import React, { useCallback } from 'react'
import styled, { ThemeProvider, css } from 'styled-components'
import classNames from 'classnames'
import { Button as ButtonBs, ButtonProps as ButtonPropsBs, Input } from '@bootstrap-styled/v4'
import { buttonsMixins, ifThen } from '@bootstrap-styled/css-mixins'
import Color from '@bootstrap-styled/color'
import { appTheme } from '../../../styles/app.theme'
import { AppBrandColors } from '../../../typings/global'

export type ButtonProps = {
    expand?: boolean
    size?: 'sm' | 'lg' | 'xl'
    color?: ButtonPropsBs['color'] | Extract<AppBrandColors, 'tertiary'>
} & Omit<ButtonPropsBs, 'ref' | 'size' | 'color'>

const ButtonUnstyled: React.FC<ButtonProps> = ({
    tag: Tag = 'button',
    expand,
    size,
    className,
    outline,
    color = 'primary',
    cssModule,
    ...props
}) => {
    const classes = classNames(
        className,
        { 'btn-expand': !!expand, [`btn-${size}`]: !!size },
        `btn${outline ? '-outline' : ''}-${color}`
    )

    const newCssModule = {
        'btn-primary': color === 'primary' && !outline ? 'btn-primary' : true,
        ...(cssModule || {})
    }

    const passTag = useCallback(
        (attrs): JSX.Element => {
            if (Tag === Input) {
                return <Tag cssModule={{ 'form-control': 'form-control-button' }} {...attrs} />
            } else {
                return <Tag {...attrs} />
            }
        },
        [Tag]
    )

    return (
        <ThemeProvider
            theme={(theme = appTheme) => ({
                ...theme,
                '$font-size-base': theme['$btn-font-size'],
                '$font-size-sm': theme['$btn-font-size-sm'],
                '$font-size-lg': theme['$btn-font-size-lg']
            })}
        >
            <ButtonBs tag={passTag} className={classes} cssModule={newCssModule} {...props} />
        </ThemeProvider>
    )
}

const Button = styled(ButtonUnstyled)`
    &.btn {
        padding-top: 0;
        padding-bottom: 0;

        .btn-icon {
            height: ${props => props.theme['$btn-icon-size']};
            margin-right: ${props => props.theme['$spacers'][2]};

            &-left {
                margin-right: 0;
                margin-left: ${props => props.theme['$spacers'][2]};
            }
        }

        &&.disabled,
        &&:disabled {
            color: ${props => props.theme['$btn-disabled-color']};
            background-color: ${props => props.theme['$btn-disabled-bg']};
            border-color: ${props => props.theme['$btn-disabled-bg']};

            &[class*='btn-outline-'] {
                background-color: ${props =>
                    Color(props.theme['$btn-disabled-bg'])
                        .lighten(0.17)
                        .hex()};
            }

            &:focus,
            &.focus {
                box-shadow: none;
            }
        }

        /* button sizes */
        &.btn-sm {
            line-height: ${props => props.theme['$btn-line-height-sm']};
        }
        &.btn-lg {
            line-height: ${props => props.theme['$btn-line-height-lg']};
        }
        &.btn-xl {
            line-height: ${props => props.theme['$btn-line-height-xl']};
            padding: ${props =>
                `${props.theme['$btn-padding-y-xl']} ${props.theme['$btn-padding-x-xl']}`};
        }

        /* colors */
        &.btn-link {
            font-weight: ${props => props.theme['$btn-font-weight']};

            &&:disabled,
            &&.disabled {
                background: transparent;
                border-color: transparent;
                color: ${props => props.theme['$gray-light']};
            }
        }
        &.btn-secondary {
            ${props =>
                ifThen(
                    Color(props.theme['$btn-secondary-bg']).isDark(),
                    css`
                        &:hover,
                        &.hover {
                            background-color: ${Color(props.theme['$btn-secondary-bg'])
                                .lightness(35)
                                .string()};
                            border-color: ${Color(props.theme['$btn-secondary-bg'])
                                .lightness(35)
                                .string()};
                        }
                    `
                )}
        }
        &.btn-tertiary {
            ${props =>
                buttonsMixins.buttonVariant(
                    props.theme['$enable-shadows'],
                    props.theme['$btn-tertiary-color'],
                    props.theme['$btn-tertiary-bg'],
                    props.theme['$btn-tertiary-border'],
                    props.theme['$btn-active-box-shadow'],
                    props.theme['$btn-box-shadow']
                )}

            &:hover, &.hover {
                color: ${props => props.theme['$white']};
                background-color: ${props => props.theme['$btn-tertiary-color']};
                border-color: ${props => props.theme['$btn-tertiary-color']};
            }
        }

        /* custom */
        &.btn-expand {
            display: block;
            width: 100%;
            padding-right: 0;
            padding-left: 0;
        }
    }
`

export default Button
